import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  QuestionAnswer as QuestionIcon,
  Comment as CommentIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { QuestionForm } from "@/components/common";
import {
  openImagePreview,
  openRichTextPreview,
} from "@/store/slices/contentPreviewSlice";
import {
  useGetVocabularyQuestionsQuery,
  useCreateVocabularyQuestionMutation,
  useUpdateVocabularyQuestionMutation,
  useDeleteVocabularyQuestionMutation,
} from "@/store/api/admin/exerciseApi";
import { uploadMediaFiles } from "@/utils/cloudinaryUpload";

export default function QuestionsDialog({ open, onClose, dayId }) {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [previousQuestion, setPreviousQuestion] = useState(null);
  const [questionFormData, setQuestionFormData] = useState({
    question: "",
    type: "multiple-choice",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
    order: 0,
  });

  // API hooks
  const { data: questionsResponse, isLoading: isQuestionsLoading } =
    useGetVocabularyQuestionsQuery({ dayId }, { skip: !dayId });

  const [createQuestion, { isLoading: isCreatingQuestion }] =
    useCreateVocabularyQuestionMutation();
  const [updateQuestion, { isLoading: isUpdatingQuestion }] =
    useUpdateVocabularyQuestionMutation();
  const [deleteQuestion, { isLoading: isDeletingQuestion }] =
    useDeleteVocabularyQuestionMutation();

  const vocabularyQuestions = questionsResponse?.data || [];
  const isAnyLoading =
    isQuestionsLoading ||
    isCreatingQuestion ||
    isUpdatingQuestion ||
    isDeletingQuestion;

  // No need to fetch questions manually - useGetVocabularyQuestionsQuery handles it

  // Update previous question when question number changes
  useEffect(() => {
    if (questionFormData?.question_number && vocabularyQuestions.length > 0) {
      const prevIndex = vocabularyQuestions.findIndex(
        (q) => q.number === questionFormData.question_number - 1
      );
      if (prevIndex >= 0) {
        setPreviousQuestion(vocabularyQuestions[prevIndex]);
      }
    }
  }, [questionFormData?.question_number, vocabularyQuestions]);

  const handleOpenQuestionDialog = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      // Find previous question for editing mode
      const currentIndex = vocabularyQuestions.findIndex(
        (q) => q._id === question._id
      );
      setPreviousQuestion(
        currentIndex > 0 ? vocabularyQuestions[currentIndex - 1] : null
      );

      const options = question.choices?.map((opt) => opt.text) || [
        "",
        "",
        "",
        "",
      ];
      
      // Find correct answer - try multiple approaches
      let correctAnswer = "";
      if (question.correctAnswer) {
        // If correctAnswer is already set (like "A", "B", etc.)
        correctAnswer = question.correctAnswer;
      } else if (question.choices) {
        // Find from choices array
        const correctChoice = question.choices.find((opt) => opt.isCorrect);
        correctAnswer = correctChoice?.value || "";
      }
      
      console.log('Editing question - correctAnswer:', correctAnswer);
      
      setQuestionFormData({
        ...question,
        type: question.type || "multiple-choice",
        options,
        correctAnswer,
        question_number: question.number,
        imagePreview: question.imageUrl || "",
        audioPreview: question.audioUrl || "",
        extraImagePreview: question.extraImageUrl || "",
      });
    } else {
      setEditingQuestion(null);
      // Set previous question to the last question in the list for new question
      setPreviousQuestion(
        vocabularyQuestions.length > 0
          ? vocabularyQuestions[vocabularyQuestions.length - 1]
          : null
      );

      const nextOrder =
        vocabularyQuestions.length > 0
          ? Math.max(...vocabularyQuestions.map((q) => q.number)) + 1
          : 1;
      setQuestionFormData({
        question: "",
        type: "multiple-choice",
        options: ["", "", "", ""],
        correctAnswer: "",
        explanation: "",
        question_number: nextOrder,
        imagePreview: "",
        audioPreview: "",
        extraImagePreview: "",
      });
    }
    setQuestionDialogOpen(true);
  };

  const handleCloseQuestionDialog = () => {
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
  };

  const handleSubmitQuestion = async () => {
    if (isSubmittingQuestion) return;
    setIsSubmittingQuestion(true);
    try {
      // Process images in rich text editors and get final data
      let currentFormData = questionFormData;
      if (formikRef.current) {
        currentFormData = await formikRef.current.processImagesAndGetData();
      }

      // Validate based on question type
      if (
        currentFormData.type === "multiple-choice" &&
        !currentFormData.correctAnswer
      ) {
        toast.error("Correct answer is required for multiple-choice questions");
        return;
      }
      if (
        currentFormData.type === "short_answer" &&
        !currentFormData.correctAnswer
      ) {
        toast.error("Correct answer is required for short answer questions");
        return;
      }

      // Upload media files directly to Cloudinary
      const mediaFiles = {
        image: currentFormData.image,
        audio: currentFormData.audio,
        extraImage: currentFormData.extraImage,
      };
      
      const uploadedUrls = await uploadMediaFiles(mediaFiles, `toeic/exercises/${dayId}`);

      // Create FormData for file uploads
      const formData = new FormData();

      // Append basic fields
      formData.append("question", currentFormData.question);
      formData.append("number", currentFormData.question_number);
      formData.append("type", currentFormData.type);
      
      // Sanitize and append content
      const sanitizedContent = currentFormData.content
        ? currentFormData.content.replace(/<[^>]*>/g, "").trim()
        : "";
      formData.append(
        "content",
        sanitizedContent.length > 0 ? currentFormData.content : ""
      );
      formData.append("explanation", currentFormData.explanation || "");
      formData.append("correctAnswer", currentFormData.correctAnswer || "");

      // Add options array only for multiple-choice questions
      if (currentFormData.type === "multiple-choice") {
        const options = currentFormData.options
          .filter(opt => opt && opt.trim()) // Filter out empty options
          .map((opt, idx) => ({
            text: opt.trim(),
            isCorrect:
              String.fromCharCode(idx + 65) === currentFormData.correctAnswer,
          }));
        
        console.log('Sending options to API:', options);
        console.log('Correct answer:', currentFormData.correctAnswer);
        
        // Send as JSON string - backend will parse it and generate values (A, B, C, D)
        formData.append("options", JSON.stringify(options));
      }

      // Handle blanks for fill-blank questions
      if (currentFormData.type === "fill-blank" && currentFormData.blanks) {
        formData.append("blanks", JSON.stringify(currentFormData.blanks));
      }

      // Append uploaded URLs or existing URLs
      ["image", "audio", "extraImage"].forEach((mediaName) => {
        const url = `${mediaName}Url`;
        if (uploadedUrls[url]) {
          formData.append(url, uploadedUrls[url]);
        } else if (Object.prototype.hasOwnProperty.call(currentFormData, url)) {
          formData.append(url, currentFormData[url] ?? "");
        }
      });

      if (editingQuestion) {
        await updateQuestion({
          questionId: editingQuestion._id,
          questionData: formData,
        }).unwrap();
        toast.success("Question updated successfully");
      } else {
        await createQuestion({
          dayId,
          questionData: formData,
        }).unwrap();
        toast.success("Question added successfully");
      }
      setQuestionDialogOpen(false);
    } catch (error) {
      console.error("Failed to save question:", error);
      toast.error(error?.data?.message || "Failed to save question");
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?"))
      return;

    try {
      await deleteQuestion(questionId).unwrap();
      toast.success("Question deleted successfully");
    } catch (error) {
      console.error("Failed to delete question:", error);
      toast.error(error?.data?.message || "Failed to delete question");
    }
  };

  const handleQuestionFormChange = (updatedFormData) => {
    console.log("Updated form data:", updatedFormData);
    setQuestionFormData(updatedFormData);
  };

  const handleImageClick = (imageUrl) => {
    dispatch(openImagePreview(imageUrl));
  };

  const handleContentClick = (content) => {
    dispatch(openRichTextPreview(content));
  };

  const isSubmitBtnDisabled = useMemo(
    () =>
      isAnyLoading ||
      isSubmittingQuestion ||
      ((questionFormData.type === "multiple-choice" ||
        questionFormData.type === "short_answer") &&
        !questionFormData.correctAnswer) ||
      (questionFormData.type === "fill-blank" &&
        (!questionFormData.blanks.length || !questionFormData.content)),
    [questionFormData, isAnyLoading, isSubmittingQuestion]
  );
  return (
    <>
      {/* Questions Management Dialog */}
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <QuestionIcon />
              <Typography variant="h5">
                Vocabulary Questions ({vocabularyQuestions.length})
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenQuestionDialog()}
              disabled={isAnyLoading}
              sx={{ mb: 2 }}
            >
              Add Question
            </Button>

            {isQuestionsLoading ? (
              <Box display="flex" justifyContent="center" my={2}>
                <CircularProgress />
              </Box>
            ) : vocabularyQuestions.length > 0 ? (
              <List>
                {vocabularyQuestions
                  // .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((question) => (
                    <ListItem key={question.id} divider>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {question.number}. {question.question}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              (
                              {question.type === "multiple-choice"
                                ? "Multiple Choice"
                                : question.type === "fill-blank"
                                ? "Fill in the Blank"
                                : question.type === "short_answer"
                                ? "Short Answer"
                                : "Unknown"}
                              )
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {question.type === "multiple-choice" &&
                                question.options && (
                                  <>
                                    Correct Answer:{" "}
                                    {
                                      question.options?.find(
                                        (opt) => opt.isCorrect
                                      )?.value
                                    }
                                    .{" "}
                                    {
                                      question.options?.find(
                                        (opt) => opt.isCorrect
                                      )?.text
                                    }
                                  </>
                                )}
                              {question.type === "short_answer" && (
                                <>Correct Answer: {question.correctAnswer}</>
                              )}
                            </Typography>
                            {question.audioUrl && (
                              <audio
                                controls
                                src={question.audioUrl}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "200px",
                                  borderRadius: "4px",
                                  width: "100%",
                                }}
                              />
                            )}
                            {question.imageUrl && (
                              <IconButton
                                aria-label="image"
                                onClick={() =>
                                  handleImageClick(question.imageUrl)
                                }
                              >
                                <ImageIcon />
                              </IconButton>
                            )}
                            {question.content && (
                              <IconButton
                                aria-label="content"
                                onClick={() =>
                                  handleContentClick(question.content)
                                }
                              >
                                <CommentIcon />
                              </IconButton>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => handleOpenQuestionDialog(question)}
                          disabled={isAnyLoading}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleDeleteQuestion(question._id)}
                          disabled={isAnyLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
              </List>
            ) : (
              <Alert severity="info">
                No questions added for this vocabulary section yet. Click "Add
                Question" to create the first question.
              </Alert>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Question Form Dialog */}
      <Dialog
        open={questionDialogOpen}
        onClose={handleCloseQuestionDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontSize: "2rem" }}>
            {editingQuestion ? "Edit Question" : "Add New Question"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <QuestionForm
            ref={formikRef}
            initialData={questionFormData}
            onChange={handleQuestionFormChange}
            previousQuestion={previousQuestion}
            showContent={true}
            isEditing={!!editingQuestion}
            allowMultipleTypes={true}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseQuestionDialog}
            disabled={isSubmittingQuestion}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitQuestion}
            variant="contained"
            disabled={isSubmitBtnDisabled}
          >
            {isSubmittingQuestion && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {editingQuestion ? "Update" : "Add"} Question
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
